// metrics.js
// Data layer for ConsoleV2's Overview tab. Fetches the collections the
// dashboard needs and computes the "real" metrics (funnel conversion,
// guest-vs-registered split, WAU, push reachability) rather than raw counts.
//
// All metrics EXCLUDE test accounts, matching the July 2026 funnel analysis:
// isAdmin users plus emails containing test / qwerty / davidlane / "+".

import {
  collection,
  getDocs,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import db from "../../Firebase";
import {
  addDaysPT,
  formatWeekLabel,
  startOfWeekMondayPT,
} from "../AnalyticsV2/analytics/date";

const DAY_MS = 24 * 60 * 60 * 1000;
const TEST_EMAIL_RE = /test|qwerty|davidlane|\+/i;

export const toDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const isTestUser = (user) =>
  user?.isAdmin === true ||
  user?.isTest === true ||
  (user?.email && TEST_EMAIL_RE.test(user.email));

const mapSnapshot = (snap) =>
  snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

const fetchEventsSince = async (collectionName, start) => {
  const q = query(
    collection(db, collectionName),
    where("createdAt", ">=", Timestamp.fromDate(start)),
    orderBy("createdAt", "asc")
  );
  const snap = await getDocs(q);
  return mapSnapshot(snap).map((row) => ({
    ...row,
    createdAt: toDate(row.createdAt),
  }));
};

// The installed firebase SDK predates getCountFromServer; these collections
// are tiny (dozens of docs), so fetching and counting is fine.
const fetchCollectionCount = async (collectionName) => {
  try {
    const snap = await getDocs(collection(db, collectionName));
    return snap.size;
  } catch (err) {
    console.error(`Count failed for ${collectionName}`, err);
    return null;
  }
};

export const WEEKS_OF_HISTORY = 12;

export const loadDashboardData = async () => {
  const trendStart = addDaysPT(
    startOfWeekMondayPT(new Date()),
    -7 * (WEEKS_OF_HISTORY - 1)
  );
  const [usersSnap, plantsSnap, waterings, chats, friendshipCount, frondleCount] =
    await Promise.all([
      getDocs(collection(db, "Users")),
      getDocs(collection(db, "Plants")),
      fetchEventsSince("Waterings", trendStart),
      fetchEventsSince("Chats", trendStart),
      fetchCollectionCount("Friendships"),
      fetchCollectionCount("Frondles"),
    ]);

  // Plant ownership lives on Plants.owner since the mid-2026 migration; the
  // Users.ownedPlants array is only reliable for accounts that never ran a
  // post-migration build. Count per owner, fall back to the legacy array.
  const ownerCounts = new Map();
  plantsSnap.docs.forEach((doc) => {
    const owner = doc.data().owner;
    if (owner) ownerCounts.set(owner, (ownerCounts.get(owner) || 0) + 1);
  });

  const users = mapSnapshot(usersSnap).map((row) => ({
    ...row,
    createdAt: toDate(row.createdAt),
    _plantCount:
      ownerCounts.get(row.id) ||
      (Array.isArray(row.ownedPlants) ? row.ownedPlants.length : 0),
  }));
  return { users, waterings, chats, friendshipCount, frondleCount };
};

// --------------------------------------------------------------------------
// Computations
// --------------------------------------------------------------------------

const plantCount = (user) =>
  typeof user._plantCount === "number"
    ? user._plantCount
    : Array.isArray(user.ownedPlants)
    ? user.ownedPlants.length
    : 0;

const wateringCountOf = (user) =>
  typeof user.wateringCount === "number" ? user.wateringCount : 0;

const chatCountOf = (user) =>
  typeof user.chatCount === "number" ? user.chatCount : 0;

const isGuest = (user) => !user.email;

const pct = (num, den) => (den > 0 ? (num / den) * 100 : 0);

const segmentStats = (users, now) => {
  const total = users.length;
  const withPlant = users.filter((u) => plantCount(u) > 0).length;
  const everWatered = users.filter((u) => wateringCountOf(u) > 0).length;
  const fivePlus = users.filter((u) => wateringCountOf(u) >= 5).length;
  const everChatted = users.filter((u) => chatCountOf(u) > 0).length;
  const wateredLast30 = users.filter((u) => {
    const last = toDate(u.lastWateringAt);
    return last && now - last.getTime() <= 30 * DAY_MS;
  }).length;
  return {
    total,
    withPlantPct: pct(withPlant, total),
    everWateredPct: pct(everWatered, total),
    fivePlusPct: pct(fivePlus, total),
    everChattedPct: pct(everChatted, total),
    wateredLast30Pct: pct(wateredLast30, total),
  };
};

// audience: "all" (every real user), "planted" (added at least one plant),
// or "watered" (watered at least once). Every stat below is computed over
// the chosen audience.
export const computeMetrics = (
  { users, waterings, chats, friendshipCount, frondleCount },
  audience = "all"
) => {
  const now = Date.now();
  const nonTestUsers = users.filter((u) => !isTestUser(u));
  const realUsers =
    audience === "watered"
      ? nonTestUsers.filter((u) => wateringCountOf(u) > 0)
      : audience === "planted"
      ? nonTestUsers.filter((u) => plantCount(u) > 0)
      : nonTestUsers;
  const testUserIds = new Set(users.filter(isTestUser).map((u) => u.id));
  const realUserById = new Map(realUsers.map((u) => [u.id, u]));

  const actions = [
    ...waterings.map((w) => ({ ...w, type: "watering" })),
    ...chats.map((c) => ({ ...c, type: "chat" })),
  ].filter((a) => a.createdAt && a.userId && realUserById.has(a.userId));

  // ---- Funnel (all-time, from Users doc counters) ----
  const funnel = [
    { label: "Signed up", count: realUsers.length },
    {
      label: "Added a plant",
      count: realUsers.filter((u) => plantCount(u) > 0).length,
    },
    {
      label: "Watered once",
      count: realUsers.filter((u) => wateringCountOf(u) > 0).length,
    },
    {
      label: "Watered 5+",
      count: realUsers.filter((u) => wateringCountOf(u) >= 5).length,
    },
  ];
  const everChatted = realUsers.filter((u) => chatCountOf(u) > 0).length;

  // ---- Active users (current window + the window before it, for deltas) ----
  const activeInWindow = (fromDaysAgo, toDaysAgo) =>
    new Set(
      actions
        .filter((a) => {
          const age = now - a.createdAt.getTime();
          return age <= fromDaysAgo * DAY_MS && age > toDaysAgo * DAY_MS;
        })
        .map((a) => a.userId)
    ).size;
  const wau = activeInWindow(7, 0);
  const wauPrev = activeInWindow(14, 7);
  const mau = activeInWindow(30, 0);
  const mauPrev = activeInWindow(60, 30);

  // ---- Signups ----
  const signupsInWindow = (fromDaysAgo, toDaysAgo) =>
    realUsers.filter((u) => {
      if (!u.createdAt) return false;
      const age = now - u.createdAt.getTime();
      return age <= fromDaysAgo * DAY_MS && age > toDaysAgo * DAY_MS;
    });
  const signups30 = signupsInWindow(30, 0);
  const signupsPrev30 = signupsInWindow(60, 30);
  const guestSignups30 = signups30.filter(isGuest).length;

  // ---- Care actions this week vs last week ----
  const actionsInWindow = (fromDaysAgo, toDaysAgo) =>
    actions.filter((a) => {
      const age = now - a.createdAt.getTime();
      return age <= fromDaysAgo * DAY_MS && age > toDaysAgo * DAY_MS;
    }).length;
  const careActions7 = actionsInWindow(7, 0);
  const careActionsPrev7 = actionsInWindow(14, 7);

  // ---- Guest vs registered ----
  const segments = {
    guest: segmentStats(realUsers.filter(isGuest), now),
    registered: segmentStats(realUsers.filter((u) => !isGuest(u)), now),
  };

  // ---- Push reachability ----
  const pushBuckets = { authorized: 0, provisional: 0, notDetermined: 0, denied: 0, unknown: 0 };
  realUsers.forEach((u) => {
    const status =
      u.notificationPermissionStatus || u.notificationSystemStatus || "unknown";
    if (pushBuckets[status] == null) pushBuckets.unknown += 1;
    else pushBuckets[status] += 1;
  });

  // ---- Weekly trends ----
  const weekKeys = [];
  const weekMap = new Map();
  let weekStart = addDaysPT(
    startOfWeekMondayPT(new Date()),
    -7 * (WEEKS_OF_HISTORY - 1)
  );
  for (let i = 0; i < WEEKS_OF_HISTORY; i += 1) {
    const key = weekStart.toISOString().slice(0, 10);
    weekKeys.push(key);
    weekMap.set(key, {
      key,
      label: formatWeekLabel(weekStart),
      waterings: 0,
      chats: 0,
      activeUsers: new Set(),
      signups: 0,
      guestSignups: 0,
    });
    weekStart = addDaysPT(weekStart, 7);
  }
  const weekKeyFor = (date) =>
    startOfWeekMondayPT(date).toISOString().slice(0, 10);
  actions.forEach((a) => {
    const wk = weekMap.get(weekKeyFor(a.createdAt));
    if (!wk) return;
    wk[a.type === "chat" ? "chats" : "waterings"] += 1;
    wk.activeUsers.add(a.userId);
  });
  realUsers.forEach((u) => {
    if (!u.createdAt) return;
    const wk = weekMap.get(weekKeyFor(u.createdAt));
    if (!wk) return;
    wk.signups += 1;
    if (isGuest(u)) wk.guestSignups += 1;
  });
  const weeks = weekKeys.map((key) => {
    const wk = weekMap.get(key);
    return { ...wk, activeUsers: wk.activeUsers.size };
  });

  // ---- Signup-cohort activation (the "is onboarding improving?" view) ----
  // Counters are all-time, but activation nearly always happens in the first
  // days, so per-cohort rates are a fair read of how each month's onboarding
  // performed. The newest cohorts are still maturing — flagged in the UI.
  const COHORT_MONTHS = 8;
  const monthKeyOf = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  const cohortKeys = [];
  {
    const d = new Date();
    d.setDate(1);
    for (let i = COHORT_MONTHS - 1; i >= 0; i -= 1) {
      const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
      cohortKeys.push(monthKeyOf(m));
    }
  }
  const cohortMap = new Map(
    cohortKeys.map((key) => [key, []])
  );
  realUsers.forEach((u) => {
    if (!u.createdAt) return;
    const key = monthKeyOf(u.createdAt);
    if (cohortMap.has(key)) cohortMap.get(key).push(u);
  });
  const cohorts = cohortKeys.map((key) => {
    const members = cohortMap.get(key);
    const total = members.length;
    return {
      key,
      total,
      guestPct: pct(members.filter(isGuest).length, total),
      withPlantPct: pct(members.filter((u) => plantCount(u) > 0).length, total),
      everWateredPct: pct(
        members.filter((u) => wateringCountOf(u) > 0).length,
        total
      ),
      fivePlusPct: pct(
        members.filter((u) => wateringCountOf(u) >= 5).length,
        total
      ),
      everChattedPct: pct(
        members.filter((u) => chatCountOf(u) > 0).length,
        total
      ),
    };
  });

  // ---- Chat concentration (LLM cost exposure) ----
  const totalChatCount = realUsers.reduce((sum, u) => sum + chatCountOf(u), 0);
  const topChatters = [...realUsers]
    .sort((a, b) => chatCountOf(b) - chatCountOf(a))
    .slice(0, 5)
    .filter((u) => chatCountOf(u) > 0)
    .map((u) => ({
      id: u.id,
      name: u.name || u.email || u.id.slice(0, 8),
      chats: chatCountOf(u),
      sharePct: pct(chatCountOf(u), totalChatCount),
    }));

  return {
    computedAt: now,
    realUserCount: realUsers.length,
    testUserCount: testUserIds.size,
    guestCount: realUsers.filter(isGuest).length,
    wau,
    wauPrev,
    mau,
    mauPrev,
    careActions7,
    careActionsPrev7,
    signups30: signups30.length,
    signupsPrev30: signupsPrev30.length,
    guestSignups30,
    cohorts,
    funnel,
    everChatted,
    everChattedPct: pct(everChatted, realUsers.length),
    segments,
    pushBuckets,
    weeks,
    friendshipCount,
    frondleCount,
    totalChatCount,
    topChatters,
    realUserById,
  };
};
